import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import "@/App.css";

// Layout
import MainLayout from "./components/Layout/MainLayout";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import DailyTrackerPage from "./pages/DailyTrackerPage";
import InvoicesPage from "./pages/InvoicesPage";
import PaymentsPage from "./pages/PaymentsPage";
import JournalEntriesPage from "./pages/JournalEntriesPage";
import ChartOfAccountsPage from "./pages/ChartOfAccountsPage";
import BanksPage from "./pages/BanksPage";
import CashLedgerPage from "./pages/CashLedgerPage";
import PurchaseReportPage from "./pages/PurchaseReportPage";
import EInvoicingPage from "./pages/EInvoicingPage";
import BankReconciliationPage from "./pages/BankReconciliationPage";
import GSTPage from "./pages/GSTPage";
import TDSPage from "./pages/TDSPage";
import ComplianceCalendarPage from "./pages/ComplianceCalendarPage";
import FinancialStatementsPage from "./pages/FinancialStatementsPage";
import AIAlertsPage from "./pages/AIAlertsPage";
import UsersPage from "./pages/UsersPage";
import RolesPage from "./pages/RolesPage";
import TeamAccessPage from "./pages/TeamAccessPage";
import AuditLogsPage from "./pages/AuditLogsPage";
import SettingsPage from "./pages/SettingsPage";
import ZohoSyncPage from "./pages/ZohoSyncPage";
import RevenueTrackerPage from "./pages/RevenueTrackerPage";
import VendorManagementPage from "./pages/VendorManagementPage";
import HRPage from "./pages/HRPage";
import PayrollPage from "./pages/PayrollPage";
import EmployeePortalPage from "./pages/EmployeePortalPage";
import SalesCreditsPage from "./pages/SalesCreditsPage";
import EmployeeMasterPage from "./pages/EmployeeMasterPage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import ClinicPortalPage from "./pages/ClinicPortalPage";
import InventoryPage from "./pages/InventoryPage";
import OrderManagementPage from "./pages/OrderManagementPage";
import BillingAnalyticsPage from "./pages/BillingAnalyticsPage";
import LeadSourceMasterPage from "./pages/LeadSourceMasterPage";
import ItemsMasterPage from "./pages/ItemsMasterPage";
import CustomersMasterPage from "./pages/CustomersMasterPage";
import AudiologistMasterPage from "./pages/AudiologistMasterPage";
import CreateSalesOrderPage from "./pages/CreateSalesOrderPage";
import CreateInvoicePage from "./pages/CreateInvoicePage";
import VendorBillsPage from "./pages/VendorBillsPage";
import VendorCreditsPage from "./pages/VendorCreditsPage";
import PurchaseOrdersPage from "./pages/PurchaseOrdersPage";
import PaymentModesPage from "./pages/PaymentModesPage";
import TransferOrdersPage from "./pages/TransferOrdersPage";
import MarginTrackerPage from "./pages/MarginTrackerPage";
import ClinicsMasterPage from "./pages/ClinicsMasterPage";
import LocationMasterPage from "./pages/LocationMasterPage";
import CreditNotesPage from "./pages/CreditNotesPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import ClinicTrendsPage from "./pages/ClinicTrendsPage";
import CustomerReportsPage from "./pages/CustomerReportsPage";
import DiscountReportPage from "./pages/DiscountReportPage";
import ItemWiseReportPage from "./pages/ItemWiseReportPage";
import InvoiceReportPage from "./pages/InvoiceReportPage";
import SalesOrderReportPage from "./pages/SalesOrderReportPage";
import AccountingReportsPage from "./pages/AccountingReportsPage";
import DailyClinicRevenuePage from "./pages/DailyClinicRevenuePage";
import AIInsightsPage from "./pages/AIInsightsPage";

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-[#64748B]">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Admin Route wrapper
function AdminRoute({ children }) {
  const { user, isAdmin, loading } = useAuth();
  
  if (loading) {
    return null;
  }
  
  if (!user || !isAdmin()) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  
  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/login" 
        element={user ? <Navigate to="/" replace /> : <LoginPage />} 
      />
      
      {/* Protected routes */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="daily-tracker" element={<DailyTrackerPage />} />
        <Route path="revenue-tracker" element={<RevenueTrackerPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="payments" element={<PaymentsPage />} />
        <Route path="journal-entries" element={<JournalEntriesPage />} />
        <Route path="chart-of-accounts" element={<ChartOfAccountsPage />} />
        <Route path="banks" element={<BanksPage />} />
        <Route path="cash-ledger" element={<CashLedgerPage />} />
        <Route path="purchase-report" element={<PurchaseReportPage />} />
        <Route path="e-invoicing" element={<EInvoicingPage />} />
        <Route path="bank-reconciliation" element={<BankReconciliationPage />} />
        <Route path="gst" element={<GSTPage />} />
        <Route path="tds" element={<TDSPage />} />
        <Route path="compliance-calendar" element={<ComplianceCalendarPage />} />
        <Route path="financial-statements" element={<FinancialStatementsPage />} />
        <Route path="ai-alerts" element={<AIAlertsPage />} />
        <Route path="audit-logs" element={<AuditLogsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="zoho-sync" element={<ZohoSyncPage />} />
        <Route path="vendor-management" element={<VendorManagementPage />} />
        <Route path="sales-credits" element={<SalesCreditsPage />} />
        <Route path="customer-reports" element={<CustomerReportsPage />} />
        <Route path="discount-report" element={<DiscountReportPage />} />
        <Route path="item-wise-report" element={<ItemWiseReportPage />} />
        <Route path="invoice-report" element={<InvoiceReportPage />} />
        <Route path="sales-order-report" element={<SalesOrderReportPage />} />
        <Route path="accounting-reports" element={<AccountingReportsPage />} />
        <Route path="daily-clinic-revenue" element={<DailyClinicRevenuePage />} />
        <Route path="hr" element={<HRPage />} />
        <Route path="payroll" element={<PayrollPage />} />
        <Route path="employee-master" element={<EmployeeMasterPage />} />
        <Route path="announcements" element={<AnnouncementsPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="order-management" element={<OrderManagementPage />} />
        <Route path="billing-analytics" element={<BillingAnalyticsPage />} />
        <Route path="lead-sources" element={<LeadSourceMasterPage />} />
        <Route path="audiologist-master" element={<AudiologistMasterPage />} />
        <Route path="items-master" element={<ItemsMasterPage />} />
        <Route path="customers" element={<CustomersMasterPage />} />
        <Route path="create-sales-order" element={<CreateSalesOrderPage />} />
        <Route path="create-invoice" element={<CreateInvoicePage />} />
        <Route path="vendor-bills" element={<VendorBillsPage />} />
        <Route path="vendor-credit" element={<VendorCreditsPage />} />
        <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
        <Route path="payment-modes" element={<PaymentModesPage />} />
        <Route path="transfer-orders" element={<TransferOrdersPage />} />
        <Route path="margin-tracker" element={<MarginTrackerPage />} />
        <Route path="clinics-master" element={<ClinicsMasterPage />} />
        <Route path="location-master" element={<LocationMasterPage />} />
        <Route path="credit-notes" element={<CreditNotesPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="clinic-trends" element={<ClinicTrendsPage />} />
        <Route path="ai-insights" element={<AIInsightsPage />} />
        
        {/* Admin only routes */}
        <Route 
          path="users" 
          element={
            <AdminRoute>
              <UsersPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="roles" 
          element={
            <AdminRoute>
              <RolesPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="team-access" 
          element={
            <AdminRoute>
              <TeamAccessPage />
            </AdminRoute>
          } 
        />
      </Route>
      
      {/* Employee Portal - Public (separate auth) */}
      <Route path="/employee" element={<EmployeePortalPage />} />
      
      {/* Clinic Portal - Public (separate auth) */}
      <Route path="/clinic" element={<ClinicPortalPage />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
